import { jsx as _jsx } from "react/jsx-runtime";
import { createElement as _createElement } from "react";
import React from 'react';
import { styled, keyframes, css } from 'styled-components';
import { Media } from '../../theme/Media';
import ActionsListItem from './ActionListItem';
import ActionsList from './ActionsList';
import AdditionalRow from './AdditionalRow';
import { TableProvider } from './context';
import { Field } from './Field';
import Footer from './Footer';
import MobileLabel from './MobileLabel';
import PositionsGroupTbodyFooter from './PositionsGroup/TbodyFooter';
import PositionsGroupTbodyHeader, { Header as TbodyHeader } from './PositionsGroup/TbodyHeader';
import ScrollableWrapper from './ScrollableWrapper';
import TableInfo from './TableInfo';
import Tbody, { Wrapper as TbodyWrapper } from './Tbody';
import TbodySeparator, { Separator as TbodySeparatorWrapper } from './TbodySeparator';
import Td, { Wrapper as TdWrapper } from './Td';
import Th from './Th';
import Tr, { Wrapper as TrWrapper } from './Tr';
const loading = keyframes `
  to {
    transform:translateX(100%)
  }
`;
const LoaderBar = styled.span `
  width: 100%;
  height: 18px;
  border-radius: 10px;
  position: relative;
  overflow: hidden;
  background: ${({ theme }) => theme.colors.grey10_1};
  &:after {
    animation: ${loading} 1.5s infinite;
    content: '';
    height: 100%;
    left: 0;
    position: absolute;
    right: 0;
    top: 0;
    transform: translateX(-100%);
    z-index: 1;
    background: linear-gradient(90deg, transparent, hsla(0, 0%, 100%, 0.3), transparent);
    animation-delay: 0.1s;
  }
`;
const Loader = ({ columnsLength, rows = 3, dataTestId, tdProps }) => (_jsx("tbody", { "data-testid": dataTestId, children: [...Array(rows)]
        .map((v1, i) => i)
        .map((i) => (_jsx(Tr, { children: [...Array(columnsLength)]
            .map((v2, j) => j)
            .map((j) => (_createElement(Td, { ...tdProps, key: `loader_td_${j}` },
            _jsx(LoaderBar, {})))) }, `loader_tr_${i}`))) }));
export const Wrapper = styled.table `
  width: 100%;
  font-size: 13px;
  > thead {
    + ${TbodySeparatorWrapper} {
      display: none;
    }
  }
  ${({ type, isCollapsedNav, theme }) => type === 'withGroups' &&
    css `
      @media ${isCollapsedNav ? Media.lgMin : Media.xlgMin} {
        > tbody._in-group:not(._is-open) > tr > td:first-child {
          border-left: 2px solid ${theme.colors.grey30_1};
        }
        > tbody._in-group:not(._is-open) > tr > td:last-child {
          border-right: 2px solid ${theme.colors.grey30_1};
        }
      }
      @media ${isCollapsedNav ? Media.mdMax : Media.lgMax} {
        > tbody._in-group:not(._is-open) {
          position: relative;
          &:before {
            content: '';
            display: block;
            position: absolute;
            top: -5px;
            bottom: -5px;
            left: -5px;
            right: -5px;
            background-color: ${theme.colors.grey30_1};
          }
          > tr {
            position: relative;
            background-color: ${theme.colors.grey00_1};
          }
        }
      }
    `}
  @media ${({ isCollapsedNav }) => (isCollapsedNav ? Media.mdMax : Media.lgMax)} {
    display: flex;
    flex-wrap: wrap;
    thead {
      display: flex;
      width: 100%;
      tr {
        width: 100%;
        margin-bottom: 0;
      }
    }
    tbody:not(${TbodyWrapper}):not(${TbodyHeader}) {
      display: flex;
      flex-wrap: wrap;
      margin: ${({ type }) => type === 'sub' && '10px 10px 0'};
      width: ${({ type }) => (type === 'sub' ? 'calc(100% - 20px)' : '100%')};
    }
    tbody {
      ${({ type, theme }) => type === 'sub' &&
    css `
          ${TrWrapper} {
            ${TdWrapper} {
              background: transparent;
            }
            background: ${theme.colors.grey00_1};
            border: 1px solid ${theme.colors.grey10_1};
            border-radius: 4px;
            ${theme.shadows.shadowTuna};
          }
        `}
    }
  }
`;
const Table = ({ children, className, dataTestId, type, isCollapsedNav = true }) => (_jsx(TableProvider, { isCollapsedNav: isCollapsedNav, children: _jsx(Wrapper, { "data-testid": dataTestId, className: className, type: type, isCollapsedNav: isCollapsedNav, children: children }) }));
const TableMemo = React.memo(Table);
const TableWrapper = (props) => _jsx(TableMemo, { ...props });
TableWrapper.Tr = Tr;
TableWrapper.Th = Th;
TableWrapper.Td = Td;
TableWrapper.MobileLabel = MobileLabel;
TableWrapper.Tbody = Tbody;
TableWrapper.Footer = Footer;
TableWrapper.Loader = Loader;
TableWrapper.LoaderBar = LoaderBar;
TableWrapper.ActionsList = ActionsList;
TableWrapper.ActionsListItem = ActionsListItem;
TableWrapper.TableInfo = TableInfo;
TableWrapper.AdditionalRow = AdditionalRow;
TableWrapper.TbodySeparator = TbodySeparator;
TableWrapper.PositionsGroupTbodyHeader = PositionsGroupTbodyHeader;
TableWrapper.PositionsGroupTbodyFooter = PositionsGroupTbodyFooter;
TableWrapper.Field = Field;
TableWrapper.ScrollableWrapper = ScrollableWrapper;
export default TableWrapper;
